/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Button from "./ui/Button";
import Text from "./ui/Text";
import TextCircleDivider from "./ui/TextCircleDivider";
import { useStepManager } from "../hooks/useStepManager";
import { useServicesSummary } from "../hooks/useServicesSummary";
import { useBookingSummary } from "../hooks/useBookingSummary";
import T from "./ui/Translation";
import Icon from "./ui/Icon";
import usePluginUtils from "../hooks/usePluginUtils";
import { isClassService } from "../utils/classes";
import FormatPrice from "./formatters/FormatPrice";
import FormatDuration from "./formatters/FormatDuration";

export const StyledFooterStepButton = styled.div`
  position: sticky;
  bottom: 16px;
  left: 0;
  width: 100%;
  margin-top: 24px;
  padding: 20px 16px;
  border-radius: 1rem;
  box-shadow: 2px 2px 15px 0 rgba(${({ theme }) => theme.colors.text.rgb}, 0.10);
  background-color: rgba(${({ theme }) => theme.colors.background.rgba});
`;

const StyledServiceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 8px;
  background: rgba(${({ theme }) => theme.colors.accentContrast.rgb}, 0.25);

  ${({ theme }) => theme.containerMedia.sm} {
    display: none;
  }
`;

const StyledButton = styled((props) => <Button {...props} />)`
  ${({ theme }) => {
    const isMobile = window.innerWidth < parseInt(theme.breakpoints.sm.replace("px", ""));
    return !isMobile ? "padding-right: 10px;" : "";
  }}
`;

const FooterStepButton = ({ services, loading }) => {
  const { navigateToNextStep } = useStepManager();
  const servicesSummary = useServicesSummary(services);
  const { total, currency } = useBookingSummary();
  const { isPluginEnabled } = usePluginUtils();
  const groupBookingEnabled = isPluginEnabled("group_booking");

  return (
    <StyledFooterStepButton>
      <StyledButton title="Continue" fullWidth disabled={loading} onClick={navigateToNextStep}>
        {servicesSummary && (
          <StyledServiceInfo>
            <Text color="accentContrast">
              {services.length}
              {" "}
              {(isClassService(services[0]) && <T>class</T>) || (services.length > 1 ? <T>services</T> : <T>service</T>)}
            </Text>

            {groupBookingEnabled && (
              <>
                <TextCircleDivider color="accentContrast" />
                <Text color="accentContrast"> {services.length > 1 ? 1 : servicesSummary.count} </Text>
                <Icon.Users color="secondary" />
              </>
            )}

            {servicesSummary.duration && (
              <>
                <TextCircleDivider color="accentContrast" />
                <Text color="accentContrast">
                  <FormatDuration value={servicesSummary.duration} type={FormatDuration.TYPE.HOUR} />
                </Text>
              </>
            )}

            {total > 0 && currency && (
              <>
                <TextCircleDivider color="accentContrast" />
                <Text color="accentContrast" weight="bold">
                  <FormatPrice value={total} currency={currency} />
                </Text>
              </>
            )}
          </StyledServiceInfo>
        )}
      </StyledButton>
    </StyledFooterStepButton>
  );
};

export default FooterStepButton;
